import { addDagen, fmtDag, verschilDagen } from './datum';
import type { ISODate } from './types';

/** SPEC §12.1: uitbreidingen bovenop de basis. */
export const MODULES = ['AGILE', 'UREN'] as const;
export type Module = (typeof MODULES)[number];
export const MODULE_LABEL: Record<Module, string> = { AGILE: 'Agile', UREN: 'Urenregistratie' };
export const MODULE_UITLEG: Record<Module, string> = {
  AGILE: 'Backlog, sprints, bord, burndown en velocity per werkpakket',
  UREN: 'Timer, weekstaat, urenrapport, goedkeuren en de Outlook-invoegtoepassing',
};

export type Plan = 'TRIAL' | 'STARTER' | 'PRO' | 'ENTERPRISE';
export const BETAALDE_PLANNEN = ['STARTER', 'PRO', 'ENTERPRISE'] as const;
export type BetaaldPlan = (typeof BETAALDE_PLANNEN)[number];
export const PLAN_LABEL: Record<Plan, string> = { TRIAL: 'Proefperiode', STARTER: 'Starter', PRO: 'Pro', ENTERPRISE: 'Enterprise' };
export const PLAN_LIMIET: Record<Plan, number> = { TRIAL: 25, STARTER: 15, PRO: 75, ENTERPRISE: 1000 };

/** SPEC §12.2 */
export type Abonnement = 'TRIAL' | 'STRIPE' | 'HANDMATIG';

/** SPEC §12.3: dagen na betaald-tot waarin een achterstallige betaling nog toegang geeft. */
export const RESPIJT_DAGEN = 14;
/** Laatste dagen van de proef waarin een melding verschijnt. */
export const PROEF_MELDING_DAGEN = 7;

/** Stripe-prijsonderdelen (SPEC §12.4). */
export const PRIJS_SLEUTELS = ['PLAN:STARTER', 'PLAN:PRO', 'PLAN:ENTERPRISE', 'MODULE:AGILE', 'MODULE:UREN', 'BTW'] as const;
export type PrijsSleutel = (typeof PRIJS_SLEUTELS)[number];

export interface LicentieBron {
  actief: boolean;
  abonnement: Abonnement;
  plan: Plan;
  modules: string[] | null;
  trialTot: ISODate | null;
  abonnementStatus: string | null;
  betaaldTot: ISODate | null;
  opzeggingPer: ISODate | null;
}

export type Toegang = 'VOL' | 'MELDING' | 'GEBLOKKEERD';

export interface Licentie {
  toegang: Toegang;
  /** korte uitleg voor de gebruiker (melding of blokkade) */
  melding: string | null;
  /** modules die nu aan staan */
  modules: Module[];
  /** dagen tot het einde van proef, respijt of opzegging (null = n.v.t.) */
  dagenOver: number | null;
}

const geldig = (m: string[] | null | undefined): Module[] => MODULES.filter((x) => (m ?? []).includes(x));

/** Modules die bij een plan horen (Enterprise: alles). */
export function modulesVan(plan: Plan, modules: string[] | null | undefined): Module[] {
  return plan === 'ENTERPRISE' || plan === 'TRIAL' ? [...MODULES] : geldig(modules);
}

/** SPEC §12.2–12.3: wat mag een organisatie vandaag. */
export function licentieVan(t: LicentieBron, vd: ISODate): Licentie {
  const blok = (melding: string): Licentie => ({ toegang: 'GEBLOKKEERD', melding, modules: [], dagenOver: null });
  if (!t.actief) return blok('Deze organisatie is niet actief. Neem contact op met de leverancier.');

  const proefLoopt = !!t.trialTot && t.trialTot >= vd;
  const proef = (): Licentie => {
    const over = verschilDagen(vd, t.trialTot!);
    return {
      toegang: over < PROEF_MELDING_DAGEN ? 'MELDING' : 'VOL',
      melding: over < PROEF_MELDING_DAGEN ? `De proefperiode loopt af ${over === 0 ? 'vandaag' : `over ${over} dag${over === 1 ? '' : 'en'}`}. Sluit een abonnement af om door te gaan.` : null,
      modules: [...MODULES],
      dagenOver: over,
    };
  };

  if (t.abonnement === 'HANDMATIG') return { toegang: 'VOL', melding: null, modules: modulesVan(t.plan, t.modules), dagenOver: null };

  if (t.abonnement === 'TRIAL') return proefLoopt ? proef() : blok('De proefperiode is afgelopen. Sluit een abonnement af om verder te werken. Je gegevens blijven bewaard tot de datum op het abonnementsscherm.');

  // STRIPE
  const mods = modulesVan(t.plan === 'TRIAL' ? 'STARTER' : t.plan, t.modules);
  const st = t.abonnementStatus ?? '';
  if (st === 'active' || st === 'trialing') {
    if (t.opzeggingPer) {
      const over = verschilDagen(vd, t.opzeggingPer);
      return { toegang: 'MELDING', melding: `Het abonnement is opgezegd en loopt tot en met ${fmtDag(t.opzeggingPer, 'jaar')}.`, modules: mods, dagenOver: over };
    }
    return { toegang: 'VOL', melding: null, modules: mods, dagenOver: null };
  }
  if (st === 'past_due' || st === 'unpaid') {
    const grens = t.betaaldTot ? addDagen(t.betaaldTot, RESPIJT_DAGEN) : null;
    if (grens && grens >= vd) {
      const over = verschilDagen(vd, grens);
      return { toegang: 'MELDING', melding: `Er staat een betaling open. Zonder betaling wordt de toegang over ${over} dag${over === 1 ? '' : 'en'} geblokkeerd.`, modules: mods, dagenOver: over };
    }
    return blok('De toegang is geblokkeerd omdat een betaling openstaat. Betaal de openstaande factuur of werk de betaalmethode bij.');
  }
  if (st === 'canceled') {
    if (t.betaaldTot && t.betaaldTot >= vd) return { toegang: 'MELDING', melding: `Het abonnement is beëindigd en loopt tot en met ${fmtDag(t.betaaldTot, 'jaar')}.`, modules: mods, dagenOver: verschilDagen(vd, t.betaaldTot) };
    return proefLoopt ? proef() : blok('Het abonnement is beëindigd. Sluit een nieuw abonnement af om verder te werken. Je gegevens blijven bewaard tot de datum op het abonnementsscherm.');
  }
  return proefLoopt ? proef() : blok('Het abonnement is nog niet actief. Rond de betaling af of neem contact op met de leverancier.');
}

/** SPEC §12.1: welke module hoort bij een API-pad (null = basis). */
export function moduleVoorPad(pad: string): Module | null {
  const p = pad.split('?')[0];
  if (/^\/api\/(agile|sprints|items)(\/|$)/.test(p) || p === '/api/mijn-sprint') return 'AGILE';
  if (p.startsWith('/api/tijd/') || p === '/api/tijd') return 'UREN';
  return null;
}

/** Paden die ook bij een blokkade werken. */
export const altijdToegestaan = (pad: string) => {
  const p = pad.split('?')[0];
  return p === '/api/ik' || p === '/api/mijn-organisaties' || p === '/api/wissel-organisatie' || p === '/api/huisstijl/logo' || p === '/api/abonnement' || p.startsWith('/api/abonnement/') || p === '/api/gezond';
};
